import React, { useCallback, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { Validation } from "../../types/form";
import { validateName } from "../../utils/helper/string";
import { FieldErrorMessage } from "../ui/Text";
import { useDebounce } from "@/hooks/useDebounce";

interface TextInputValidation extends Validation {
  excludeDomains?: string[];
  validateName?: boolean;
}
interface TextInputProps {
  label: string;
  field: string;
  defaultValue?: string;
  placeholder: string;
  disabled?: boolean;
  validation: TextInputValidation;
  setValueOnDisabled?: boolean;
}

const TextInput: React.FC<TextInputProps> = ({ label, field, disabled = false, validation, placeholder, defaultValue, setValueOnDisabled = false }) => {
  const {
    register,
    setValue,
    formState: { errors },
  } = useFormContext();
  // console.log({ errors });
  const errorMessage: any = field.split(".").reduce((acc: any, part) => acc?.[part], errors)?.message;

  const validateEmailDomain = (value: string): string | null => {
    if (validation?.excludeDomains) {
      const domain = value.split("@")[1];
      if (validation.excludeDomains.includes(domain)) {
        return `Email addresses from ${domain} are not allowed.`;
      }
    }
    return null;
  };
  const debouncedBlur = useDebounce((value: string) => {
    setValue(field, value.trim(), { shouldValidate: true });
  }, 300);

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      debouncedBlur(e.target.value);
    },
    [debouncedBlur]
  );

  const getRegisterOptions = useCallback(() => {
    const registerOptions: any = {};
    const minLength = validation?.minLength;
    const maxLength = validation?.maxLength || 50;
    // If field is disabled
    if (disabled && !setValueOnDisabled) {
      return {};
    }
    if (validation?.required) {
      registerOptions.required = "This field is required.";
    }
    if (minLength && maxLength && minLength === maxLength) {
      const exactLengthMessage = `Exactly ${minLength} characters are required.`;
      registerOptions.minLength = { value: minLength, message: exactLengthMessage };
      registerOptions.maxLength = { value: maxLength, message: exactLengthMessage };
    } else {
      if (minLength) {
        registerOptions.minLength = {
          value: minLength,
          message: `The minimum character allowed is ${minLength}.`,
        };
      }
      if (maxLength) {
        registerOptions.maxLength = {
          value: maxLength,
          message: `The maximum character allowed is ${maxLength}.`,
        };
      }
    }
    if (validation?.pattern) {
      registerOptions.validate = {
        pattern: (value: string) => {
          const patternValue = validation?.pattern?.value;
          if (!patternValue) throw Error("Invalid Pattern");
          const regex = new RegExp(patternValue);

          if (regex.test(value)) {
            if (validation?.excludeDomains) {
              return validateEmailDomain(value);
            }
            return true;
          } else {
            if (!validation?.required && !value) return true;
            return validation.pattern?.message || "Invalid format";
          }
        },
      };
    }
    if (validation?.validateName) {
      registerOptions.validate = {
        ...registerOptions.validate,
        name: validateName(label),
      };
    }

    return registerOptions;
  }, []);

  useEffect(() => {
    if (defaultValue) {
      if (!disabled) {
        setValue(field, defaultValue, { shouldValidate: false, shouldDirty: false });
      }
      if (disabled && setValueOnDisabled) {
        setValue(field, defaultValue, { shouldValidate: false, shouldDirty: false });
      }
    }
  }, [defaultValue]);

  return (
    <div className="">
      <input
        type="text"
        id={label + "_text"}
        placeholder={placeholder}
        className={`w-full px-3 py-7 text-text-default text-13 rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500  ${
          errorMessage ? "border-text-error" : null
        } ${disabled ? "cursor-not-allowed" : "cursor-text"}`}
        {...register(field, {
          ...getRegisterOptions(),
        })}
        onBlur={handleBlur}
        disabled={disabled}
        defaultValue={defaultValue}
        autoComplete="off"
        // adding +1 to show error
        maxLength={(validation?.maxLength || 50) + 1}
      />
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
    </div>
  );
};

export default TextInput;
